/**
 * Firebase configuration for the Menu Builder Checkout Widget.
 * Shares the same Firebase project as the admin panel.
 * Used for:
 * - Anonymous auth during guest checkout
 * - Realtime Database for order tracking
 */

import { initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app'
import { getAuth, onAuthStateChanged, onIdTokenChanged, type Auth, type User } from 'firebase/auth'
import { getDatabase, type Database } from 'firebase/database'
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics'

const firebaseApiKey = import.meta.env.VITE_FIREBASE_API_KEY || ''
const firebaseProjectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || ''
const firebaseAuthDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || ''
const firebaseStorageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || ''
const firebaseMessagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || ''
const firebaseAppId = import.meta.env.VITE_FIREBASE_APP_ID || ''
const firebaseDatabaseUrl = import.meta.env.VITE_FIREBASE_DATABASE_URL || ''
const firebaseMeasurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || ''

const firebaseConfig: FirebaseOptions = {
  apiKey: firebaseApiKey,
  authDomain: firebaseAuthDomain,
  projectId: firebaseProjectId,
  storageBucket: firebaseStorageBucket,
  messagingSenderId: firebaseMessagingSenderId,
  appId: firebaseAppId,
  ...(firebaseDatabaseUrl ? { databaseURL: firebaseDatabaseUrl } : {}),
  ...(firebaseMeasurementId ? { measurementId: firebaseMeasurementId } : {}),
}

export const isFirebaseConfigured = !!(firebaseConfig.apiKey && firebaseConfig.projectId)

let app: FirebaseApp
let auth: Auth
let database: Database
let analytics: Analytics | null

const shouldDebugAuth = import.meta.env.DEV

const describeUser = (user: User | null): string => {
  if (!user) return 'signed out'
  return `signed in (uid: ${user.uid}, anonymous: ${user.isAnonymous})`
}

if (isFirebaseConfigured) {
  try {
    app = initializeApp(firebaseConfig)
    auth = getAuth(app)
    database = getDatabase(app)
    analytics = null

    if (shouldDebugAuth) {
      onAuthStateChanged(auth, (user) => {
        console.warn('[Firebase Auth] state:', describeUser(user))
      })
      onIdTokenChanged(auth, (user) => {
        console.warn('[Firebase Auth] token:', describeUser(user))
      })
    }

    // Analytics disabled in development — too noisy in console
    if (firebaseMeasurementId && !import.meta.env.DEV) {
      isSupported()
        .then((supported) => {
          if (!supported) return
          analytics = getAnalytics(app)
        })
        .catch(() => {})
    }
  } catch (error) {
    console.warn('[Firebase] Initialization failed:', error)
    app = {} as FirebaseApp
    auth = { currentUser: null } as unknown as Auth
    database = {} as Database
    analytics = null
  }
} else {
  console.warn('[Firebase] Missing VITE_FIREBASE_API_KEY / VITE_FIREBASE_PROJECT_ID — auth disabled')
  app = {} as FirebaseApp
  auth = { currentUser: null } as unknown as Auth
  database = {} as Database
  analytics = null
}

export { app, auth, database, analytics }
export default app
